import { useState } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { useForm, ValidationError } from "@formspree/react";
import api from "../../lib/api";
import { submitToFormspree } from "../../lib/formspree";
import { ArrowUpRight } from "@phosphor-icons/react";

export default function ContactForm() {
    const [data, setData] = useState({ name: "", email: "", company: "", phone: "", subject: "", message: "" });
    const [loading, setLoading] = useState(false);
    const [, , formspreeReset] = useForm("mwvzvlqa"); // hook keeps state & gives reset

    const update = (k) => (e) => setData((d) => ({ ...d, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const payload = {
                _subject: `Windermere · Contact · ${data.subject || data.name}`,
                _form: "contact",
                ...data,
            };
            const [fsOk, mongoRes] = await Promise.allSettled([
                submitToFormspree(payload),
                api.post("/contact", data),
            ]);
            const success = fsOk.status === "fulfilled" && fsOk.value;
            const dbSuccess = mongoRes.status === "fulfilled";
            if (success || dbSuccess) {
                toast.success(mongoRes?.value?.data?.message || "Thank you. Our team will respond within 24 hours.");
                setData({ name: "", email: "", company: "", phone: "", subject: "", message: "" });
                formspreeReset?.();
            } else {
                toast.error("Could not submit inquiry. Please try again or email us directly.");
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <motion.form
            onSubmit={submit}
            data-testid="contact-form"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="wms-glass p-7 md:p-10"
        >
            <span className="wms-overline">Corporate Inquiry</span>
            <h3 className="mt-3 font-display text-2xl md:text-3xl">Send us a message</h3>

            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-px bg-white/5 border border-white/5">
                {[
                    ["name", "Full Name *", "text", true],
                    ["email", "Email *", "email", true],
                    ["company", "Company", "text", false],
                    ["phone", "Phone", "tel", false],
                    ["subject", "Subject", "text", false],
                ].map(([k, l, t, req]) => (
                    <label key={k} className={`bg-[#040914] p-4 ${k === "subject" ? "md:col-span-2" : ""}`}>
                        <div className="text-[10px] tracking-[0.28em] uppercase text-white/40">{l}</div>
                        <input
                            type={t}
                            required={req}
                            name={k}
                            value={data[k]}
                            onChange={update(k)}
                            data-testid={`contact-input-${k}`}
                            className="mt-2 w-full bg-transparent border-0 text-white outline-none placeholder-white/20"
                        />
                    </label>
                ))}
                <label className="md:col-span-2 bg-[#040914] p-4">
                    <div className="text-[10px] tracking-[0.28em] uppercase text-white/40">Message *</div>
                    <textarea
                        rows={5}
                        required
                        name="message"
                        value={data.message}
                        onChange={update("message")}
                        data-testid="contact-message"
                        className="mt-2 w-full bg-transparent border-0 text-white outline-none placeholder-white/20 resize-none"
                        placeholder="How can our team help?"
                    />
                </label>
            </div>

            <button
                type="submit"
                disabled={loading}
                data-testid="contact-submit-btn"
                className="mt-8 inline-flex items-center gap-3 px-7 py-4 bg-white text-[#040914] text-xs uppercase tracking-[0.24em] font-medium hover:bg-[#48CAE4] transition-colors disabled:opacity-60"
            >
                {loading ? "Sending..." : "Send Inquiry"}
                <ArrowUpRight size={16} weight="bold" />
            </button>
        </motion.form>
    );
}
